import { PROVIDER_LABELS } from '../config/reportRequiredProviders';

/**
 * Plan section 7 — one place that answers "why is this section empty?".
 *
 * `EvidenceStatus: 'unavailable'` covers situations that need opposite actions,
 * and every surface rendering one rendered a single sentence asserting the
 * provider answered and telling the user to upload a document. A 429 and an
 * expired API key both read as "we asked and there is nothing there".
 *
 * WHAT THE PRODUCER ACTUALLY WRITES (src/services/siteEvidence.ts). The rich
 * `ProviderFailureMode` is NOT persisted on the section — it is collapsed by
 * `attemptResultFromFailureMode` into `attemptResult`, which is lossy:
 *
 *   rate-limited          -> 'rate-limited'
 *   permanent-unavailable -> 'empty'        <- NOT the same as a genuine empty answer
 *   skipped               -> 'not-attempted'
 *   transient-failure     -> 'http-error'
 *   misconfigured         -> 'http-error'   <- not retryable
 *   auth-missing          -> 'http-error'   <- not retryable
 *   auth-invalid          -> 'http-error'   <- not retryable
 *
 * What survives the collapse is `emptyReason`, whose text is written per failure
 * mode by `reasonFor` in the same file. So the mode is recovered from the reason
 * text. That is a coupling to another module's copy, which is why
 * `__tests__/evidenceUnavailableReason.test.ts` asserts against the producer's
 * exact strings: if that copy changes, the test fails rather than the
 * classification silently degrading to the generic case.
 *
 * `unavailableCause` is checked too, but note that nothing in the codebase
 * currently writes 'non-us-coverage' or 'not-attempted' to it — the only value
 * ever stamped is 'intent-missing', by stampBtmGroundingFromIntent. The checks
 * are kept because the field is the declared home for this information and
 * costs nothing; the `emptyReason` paths are what actually fire today.
 *
 * This module is DESCRIPTIVE ONLY. It decides no status, no blocking and no
 * permanence. An earlier version of this work routed a coverage status from a
 * message-level signal, and that reached `dataConfidenceCalculator` and moved a
 * site's composite and confidence tag. A message must never move a score.
 */

export type UnavailableKind =
  | 'provider-failed'
  | 'needs-admin-config'
  | 'not-attempted'
  | 'out-of-coverage'
  | 'empty-answer';

export interface UnavailableSectionFields {
  attemptResult?: 'success' | 'empty' | 'http-error' | 'timeout' | 'rate-limited' | 'not-attempted';
  unavailableCause?: 'provider-empty' | 'intent-missing' | 'non-us-coverage' | 'not-attempted';
  emptyReason?: string;
  attemptedProvider?: string;
}

export interface UnavailableDescription {
  kind: UnavailableKind;
  /** True only when re-running the collection unchanged could succeed. */
  retryable: boolean;
  /** Provider display name where one is known, else the stamped id, else null. */
  provider: string | null;
  /** The producer's reason text, trailing period removed so it can be embedded. */
  reason: string | null;
}

/** siteEvidence.ts reasonFor(): auth-missing, auth-invalid, misconfigured. */
const NEEDS_ADMIN_CONFIG = /API key is not configured|API key was rejected upstream|integration is broken/i;
/** siteEvidence.ts reasonFor(): permanent-unavailable. */
const NO_COVERAGE = /no coverage for this location/i;
/** siteEvidence.ts reasonFor(): skipped. */
const SKIPPED = /^Provider skipped/i;

export function describeUnavailableSection(
  section: UnavailableSectionFields | null | undefined,
): UnavailableDescription {
  const stamped = section?.attemptedProvider?.trim() || null;
  const provider = stamped ? (PROVIDER_LABELS[stamped] ?? stamped) : null;
  // Producer copy already ends in a period; embedding it mid-sentence otherwise
  // yields "… — Provider returned no climate data. — upload a document…".
  const reason = section?.emptyReason?.trim().replace(/\.\s*$/, '') || null;
  const raw = section?.emptyReason ?? '';
  const attempt = section?.attemptResult;
  const cause = section?.unavailableCause;

  if (NEEDS_ADMIN_CONFIG.test(raw)) {
    return { kind: 'needs-admin-config', retryable: false, provider, reason };
  }
  if (cause === 'non-us-coverage' || NO_COVERAGE.test(raw)) {
    return { kind: 'out-of-coverage', retryable: false, provider, reason };
  }
  if (attempt === 'not-attempted' || cause === 'not-attempted' || SKIPPED.test(raw)) {
    return { kind: 'not-attempted', retryable: false, provider, reason };
  }
  if (attempt === 'http-error' || attempt === 'timeout' || attempt === 'rate-limited') {
    return { kind: 'provider-failed', retryable: true, provider, reason };
  }
  // 'empty' / 'provider-empty' / nothing stamped. An unstamped legacy section is
  // indistinguishable from a genuine empty answer, so it keeps the wording those
  // surfaces have always used rather than claiming a failure it cannot evidence.
  return { kind: 'empty-answer', retryable: false, provider, reason };
}

/** One sentence naming the situation, the provider and the producer's reason. */
export function unavailableSentence(
  section: UnavailableSectionFields | null | undefined,
  opts: { subject?: string } = {},
): string {
  const { kind, provider, reason } = describeUnavailableSection(section);
  const who = provider ? `The provider (${provider})` : 'The provider';
  const subject = opts.subject ? `${opts.subject}: ` : '';
  const because = reason ? ` — ${reason}` : '';
  switch (kind) {
    case 'needs-admin-config':
      return `${subject}${who} is not configured for this deployment${because}. Re-running will fail the same way; an admin needs to fix the credential or integration first.`;
    case 'provider-failed':
      return `${subject}${who} failed on the last attempt${because}. This is a provider fault, not missing data for this site — retry the collection; if it keeps failing it needs attention rather than a document.`;
    case 'not-attempted':
      return `${subject}${who} was never called for this section${because} — it was skipped at dispatch. Nothing has been queried yet, so an admin needs to enable it before a retry can help.`;
    case 'out-of-coverage':
      return `${subject}${who} does not cover this site's location${because}. Retrying will not change that — supply this data as a document, or ask an admin for a coverage waiver.`;
    case 'empty-answer':
    default:
      return `${subject}${who} was queried and returned no data for this site${because} — upload a document with this data or contact your admin.`;
  }
}
